import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    TextField,
    InputAdornment,
    Grid,
    Card,
    CardContent,
    CardActionArea,
    Chip,
    CircularProgress,
    Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';

const CompanySearchPage = () => {
    const [companies, setCompanies] = useState([]);
    const [filteredCompanies, setFilteredCompanies] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const companiesPerPage = 12;

    const navigate = useNavigate();

    // Fetch companies from the API
    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${process.env.REACT_APP_BACK_END_URL}/coding-problems/companies`);

                if (!response.ok) {
                    throw new Error(`Error fetching companies: ${response.status}`);
                }

                const data = await response.json();
                setCompanies(data);
                setFilteredCompanies(data);
                setTotalPages(Math.ceil(data.length / companiesPerPage));
                setLoading(false);
            } catch (err) {
                console.error('Error fetching companies:', err);
                setError('Failed to load companies. Please try again later.');
                setLoading(false);
            }
        };

        fetchCompanies();
    }, []);

    // Filter companies based on search query
    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredCompanies(companies);
            setTotalPages(Math.ceil(companies.length / companiesPerPage));
        } else {
            const filtered = companies.filter(company =>
                company.name.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredCompanies(filtered);
            setTotalPages(Math.ceil(filtered.length / companiesPerPage));
            setPage(1); // Reset to first page when filtering
        }
    }, [searchQuery, companies]);

    // Get current page companies
    const getCurrentPageCompanies = () => {
        const startIndex = (page - 1) * companiesPerPage;
        const endIndex = startIndex + companiesPerPage;
        return filteredCompanies.slice(startIndex, endIndex);
    };

    // Handle company click
    const handleCompanyClick = (company) => {
        navigate(`/company/${company.id}`);
    };

    // Handle page change
    const handlePageChange = (event, value) => {
        setPage(value);
        window.scrollTo(0, 0);
    };

    // Random colors for company cards
    const getRandomColor = (index) => {
        const colors = [
            '#2196f3', '#4caf50', '#ff9800', '#f44336',
            '#9c27b0', '#3f51b5', '#009688', '#607d8b'
        ];
        return colors[index % colors.length];
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box my={4}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    LeetCode Company Problems
                </Typography>

                <Typography variant="h6" component="h2" gutterBottom align="center" color="textSecondary">
                    Find coding problems asked by top tech companies
                </Typography>

                <Box my={4}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search for a company..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {filteredCompanies.length === 0 ? (
                    <Box textAlign="center" my={4}>
                        <Typography variant="h6">No companies found matching "{searchQuery}"</Typography>
                    </Box>
                ) : (
                    <>
                        <Grid container spacing={3}>
                            {getCurrentPageCompanies().map((company, index) => (
                                <Grid item xs={12} sm={6} md={4} key={company.id}>
                                    <Card
                                        elevation={3}
                                        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                    >
                                        <CardActionArea
                                            onClick={() => handleCompanyClick(company)}
                                            sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                                        >
                                            <Box
                                                sx={{
                                                    height: 120,
                                                    bgcolor: getRandomColor(index),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Typography variant="h5" component="div" color="white" fontWeight="bold">
                                                    {company.name.split(' ')[0]}
                                                </Typography>
                                            </Box>
                                            <CardContent sx={{ flexGrow: 1 }}>
                                                <Typography variant="h6" component="div" gutterBottom>
                                                    {company.name}
                                                </Typography>
                                                <Box display="flex" alignItems="center" mt={1}>
                                                    <Chip
                                                        label={`${company.problem_count} Problems`}
                                                        color="primary"
                                                        size="small"
                                                        sx={{ mr: 1 }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        Last updated: {new Date(company.last_updated).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        <Box display="flex" justifyContent="center" mt={4}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                            />
                        </Box>
                    </>
                )}
            </Box>
        </Container>
    );
};

export default CompanySearchPage;